import { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import {
  CalendarClock,
  type LucideIcon,
  NotebookPen,
  User,
  UserPlus,
  X,
} from "lucide-react";
import { FormProvider } from "react-hook-form";

import { FormField, inputCls } from "../../../components";
import { useMeetingMinutesForm } from "./hooks/useMeetingMinutesForm";
import { flattenMeetingMinutes } from "./MeetingMinutesForm.rules";
import { MeetingMinutesFormProps } from "./MeetingMinutesForm.types";
import { QueryContact } from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { cn } from "@/lib/utils";

/** Visual section card with icon + title header. */
function FormCard({
  icon: Icon,
  title,
  description,
  className,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "rounded-xl border border-gray-200 dark:border-gray-700/70",
        "bg-white/60 dark:bg-gray-900/40 backdrop-blur-[1px]",
        "shadow-sm",
        className
      )}
    >
      <header className="flex items-start gap-3 px-4 sm:px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
            {title}
          </h3>
          {description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {description}
            </p>
          )}
        </div>
      </header>
      <div className="px-4 sm:px-5 py-4">{children}</div>
    </section>
  );
}

export function MeetingMinutesForm({
  onDocumentCreated,
  onClose,
  contactName,
  advisorName,
}: MeetingMinutesFormProps) {
  const form = useMeetingMinutesForm();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = form;

  const [meetDateTime, setMeetDateTime] = useState<Dayjs>(dayjs());

  // Participants kept as a list internally; serialized to comma-separated
  // string in `docRefMeetPart` (same output format as before).
  const [participants, setParticipants] = useState<string[]>(
    () => [contactName, advisorName].filter(Boolean) as string[]
  );
  const [participantInput, setParticipantInput] = useState("");

  const { data: contactsData, loading: loadingContacts } =
    useGetSavedQuery2<QueryContact>("advPortalContactsSimple", {
      contactName: "%",
    });

  const contactOptions = useMemo(() => {
    const list = (contactsData || [])
      .map((c) => c.name)
      .filter((n): n is string => Boolean(n));
    return Array.from(new Set(list)).sort((a, b) =>
      a.localeCompare(b, "no", { sensitivity: "base" })
    );
  }, [contactsData]);

  const addParticipant = (raw: string) => {
    const name = raw.trim();
    if (!name) return;
    setParticipants((prev) =>
      prev.some((p) => p.toLowerCase() === name.toLowerCase())
        ? prev
        : [...prev, name]
    );
    setParticipantInput("");
  };

  const removeParticipant = (name: string) => {
    setParticipants((prev) => prev.filter((p) => p !== name));
  };

  // Keep form value in sync with the chip list
  useEffect(() => {
    setValue("docRefMeetPart", participants.join(", "));
  }, [participants, setValue]);

  const syncDateTime = (dt: Dayjs) => {
    setValue("docRefMeetDate", dt.format("DD.MM.YYYY HH:mm"));
  };

  // Set initial value on mount
  useEffect(() => {
    setValue("docRefMeetDate", meetDateTime.format("DD.MM.YYYY HH:mm"));
    setValue("docRefMeetPlace", "Oslo");
    // participants are seeded from props via initial state above
    // only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data: Parameters<typeof flattenMeetingMinutes>[0]) => {
    onDocumentCreated?.(flattenMeetingMinutes(data));
    onClose?.();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormCard
          icon={CalendarClock}
          title="Meeting details"
          description="Where, when and who attended"
        >
          <div className="grid gap-x-6 gap-y-3 md:grid-cols-2">
            {/* Left column — when & where */}
            <div className="space-y-3">
              <FormField labelWidth={110} label="Date / time:">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    value={meetDateTime}
                    onChange={(d) => {
                      if (d?.isValid()) {
                        setMeetDateTime(d);
                        syncDateTime(d);
                      }
                    }}
                    format="DD.MM.YYYY HH:mm"
                    ampm={false}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { width: "100%" },
                      },
                    }}
                  />
                </LocalizationProvider>
              </FormField>

              <FormField labelWidth={110} label="Place:">
                <input
                  type="text"
                  {...register("docRefMeetPlace")}
                  className={inputCls}
                />
              </FormField>
            </div>

            {/* Right column — participants */}
            <div>
              <div className="mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                Participants
              </div>
              <div className="space-y-2">
                {participants.length > 0 && (
                  <ul className="flex flex-wrap gap-1.5">
                    {participants.map((name) => (
                      <li
                        key={name}
                        className="group inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded-full bg-blue-50 dark:bg-blue-950/40 border border-blue-200/70 dark:border-blue-800/60 text-blue-700 dark:text-blue-300 text-xs font-medium"
                      >
                        <User className="h-3 w-3 opacity-70" />
                        <span className="max-w-[200px] truncate">{name}</span>
                        <button
                          type="button"
                          onClick={() => removeParticipant(name)}
                          aria-label={`Remove ${name}`}
                          className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-blue-500/70 hover:bg-blue-200/60 hover:text-blue-700 dark:text-blue-400/70 dark:hover:bg-blue-900/60 dark:hover:text-blue-200 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <Autocomplete<string, false, false, true>
                  freeSolo
                  size="small"
                  options={contactOptions}
                  loading={loadingContacts}
                  inputValue={participantInput}
                  onInputChange={(_, v, reason) => {
                    if (reason !== "reset") setParticipantInput(v);
                  }}
                  filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    const selected = new Set(
                      participants.map((p) => p.toLowerCase())
                    );
                    const filtered = opts.filter(
                      (o) =>
                        !selected.has(o.toLowerCase()) &&
                        (!q || o.toLowerCase().includes(q))
                    );
                    return filtered.slice(0, 50);
                  }}
                  onChange={(_, value) => {
                    if (typeof value === "string") addParticipant(value);
                  }}
                  sx={{ width: "100%" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search contacts or type a name…"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && participantInput.trim()) {
                          e.preventDefault();
                          addParticipant(participantInput);
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <UserPlus className="h-3.5 w-3.5 ml-1 mr-0.5 text-gray-400" />
                        ),
                      }}
                    />
                  )}
                />

                {/* Hidden bound field so RHF keeps the value in form state */}
                <input type="hidden" {...register("docRefMeetPart")} />
              </div>
            </div>
          </div>
        </FormCard>

        <FormCard
          icon={NotebookPen}
          title="Minutes"
          description="Notes from the meeting"
        >
          <FormField labelWidth={160} label="Meeting minutes:">
            <textarea
              {...register("docRefDet")}
              rows={8}
              className={`${inputCls} resize-y`}
            />
          </FormField>
        </FormCard>

        {Object.keys(errors).length > 0 && (
          <p className="text-xs text-red-500">
            Please fill in all required fields.
          </p>
        )}

        {/* Footer actions — sticky to dialog bottom */}
        <div className="sticky bottom-0 -mx-4 px-4 py-3 mt-2 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-gray-900/80 flex justify-end gap-3 z-10">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="px-5 py-2 rounded-md bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold shadow-sm transition-colors"
          >
            Add to document
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
